/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { createPortal } from 'react-dom';
import {
    XMarkIcon,
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';

export type ConfirmModalVariant = 'danger' | 'warning' | 'info';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: ConfirmModalVariant;
    isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    isLoading = false,
}) => {
    if (!isOpen) return null;

    const getVariantStyles = () => {
        switch (variant) {
            case 'danger':
                return {
                    iconBg: 'bg-red-500/20',
                    iconColor: 'text-red-400',
                    buttonBg: 'bg-red-500 hover:bg-red-600',
                };
            case 'warning':
                return {
                    iconBg: 'bg-amber-500/20',
                    iconColor: 'text-amber-400',
                    buttonBg: 'bg-amber-500 hover:bg-amber-600',
                };
            case 'info':
                return {
                    iconBg: 'bg-blue-500/20',
                    iconColor: 'text-blue-400',
                    buttonBg: 'bg-blue-500 hover:bg-blue-600',
                };
        }
    };

    const styles = getVariantStyles();

    const renderIcon = () => {
        const iconClass = `w-6 h-6 ${styles.iconColor}`;
        switch (variant) {
            case 'danger':
                return <ExclamationCircleIcon className={iconClass} />;
            case 'warning':
                return <ExclamationTriangleIcon className={iconClass} />;
            case 'info':
                return <InformationCircleIcon className={iconClass} />;
        }
    };

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="flex items-start justify-between p-6 pb-0">
                    <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-full ${styles.iconBg}`}>
                            {renderIcon()}
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">{title}</h3>
                            <p className="text-sm text-gray-400 mt-1">{message}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-600 hover:text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Actions */}
                <div className="flex gap-3 p-6 pt-6">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-xl transition-colors disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 ${styles.buttonBg}`}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Processing...
                            </span>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default ConfirmModal;
